import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ReceiptMoney24Regular } from '@fluentui/react-icons';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { StatusPill } from '../../../theme/components/StatusPill';
import { formatCurrencyAmount } from '../../../commons/currency/Currency.Functions';
import { useSettlements } from '../Dashboard.Hooks';
import { WidgetProps } from '../Dashboard.Types';
import { WidgetData } from './WidgetData';
import { pendingApprovalSettlements, settlementAmount } from './Widgets.Functions';

const formatDate = (value: Date | string | undefined): string => (value ? moment(value).format('DD/MM/YYYY') : '—');

/** Liquidaciones terminadas que esperan confirmación o descarte, las más nuevas primero. */
export const PendingSettlementsWidget = (_props: WidgetProps) => {
  const { t } = useTranslation();
  const settlements = useSettlements();
  const pending = React.useMemo(() => pendingApprovalSettlements(settlements.list), [settlements.list]);

  return (
    <WidgetData loading={settlements.loading} failed={settlements.failed} error={settlements.error} onRetry={settlements.reload}>
      {pending.length === 0 ? (
        <GuideEmptyState
          icon={<ReceiptMoney24Regular />}
          title={t('dashboard.widgets.pendingSettlements.empty')}
          text={t('dashboard.widgets.pendingSettlements.emptyText')}
          data-testid="pending-settlements-empty"
        />
      ) : (
        <ul className="dbw-list" data-testid="pending-settlements-list">
          {pending.map((settlement) => {
            const { amount, currencyCode } = settlementAmount(settlement);
            return (
              <li key={settlement.id} className="dbw-list__row">
                <div className="dbw-list__main">
                  <Link to={`/settlements/${settlement.id}`} className="dbw-list__title" data-testid={`pending-settlement-${settlement.id}`}>
                    {settlement.name || settlement.identifier}
                  </Link>
                  <span className="dbw-list__meta">
                    {settlement.identifier}
                    {settlement.settlementDateRangeInit && (
                      <>
                        {' · '}
                        {t('dashboard.widgets.pendingSettlements.period')} {formatDate(settlement.settlementDateRangeInit)} – {formatDate(settlement.settlementDateRangeEnd)}
                      </>
                    )}
                  </span>
                </div>
                <div className="dbw-list__side">
                  <span className="dbw-list__amount">{amount === undefined ? '—' : formatCurrencyAmount(amount, currencyCode)}</span>
                  <StatusPill tone="warning" size="small">
                    {t('SettlementStatus.finished')}
                  </StatusPill>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetData>
  );
};

export default PendingSettlementsWidget;
